import { FC, useEffect, useLayoutEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import ActionButtons, {
    IActionButton,
} from "../../../assets/components/ActionButtons/ActionButtons";
import ImageSprite from "../../../assets/components/ImageSprite/ImageSprite";
import CalendarImg from "../../../assets/components/DataPiker/Img/calendar_sprite.svg";
import "../../../assets/components/DataPiker/DataPiker.scss";
import "./IoloLicenseKeysSubRow.scss";
import {
    useDisableIoloLicenseMutation,
    useUpdateIoloLicenseExpirationMutation,
    useGetHistoryLicenseQuery,
    IoloDetailInfo,
} from "../../../redux/RTKQuery/api";
import { useAppDispatch } from "../../../assets/hooks/hooks";
import { useNotification } from "../../../assets/hooks/useNotification";
import IoloLicenseKeysInformPanel from "./InformPanel/IoloLicenseKeysInformPanel";
import IoloLicenseKeyHistory from "../IoloLicenseKeyHistory/IoloLicenseKeyHistory";
import { FetchLicenseHistory } from "../IoloLicenseKeyHistory/historyModel";
import ActivationLimitEditorActionButtons from "../../LicenseKeys/LicenseKeysSubRow/LicenseKeysLicenseActivations/ActivationLimitEditorActionButtons/ActivationLimitEditorActionButtons";
import { toast } from "react-toastify";
import { uiSlicedActions } from "../../../redux/ui-slice";

const unixToDate = (ts: number | null | undefined): Date | null =>
    ts ? moment.unix(ts).toDate() : null;

const IoloLicenseKeysSubRow: FC<IoloDetailInfo> = ({ ioloInfo }) => {
    const dispatch = useAppDispatch();
    const [disableIoloLicense, { isLoading: isDisabling }] =
        useDisableIoloLicenseMutation();
    const [updateIoloExpiration, { isLoading: isUpdatingExpiration }] =
        useUpdateIoloLicenseExpirationMutation();
    const { ConfirmationMessageBox } = useNotification();

    const licenseKey = ioloInfo?.licenseKey ?? "";
    const initialExpiration = unixToDate(ioloInfo?.expirationDate);

    const [expirationDate, setExpirationDate] = useState<Date | null>(
        initialExpiration,
    );

    useEffect(() => {
        setExpirationDate(initialExpiration);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ioloInfo?.expirationDate]);

    const isDirty =
        (initialExpiration?.getTime() ?? null) !==
        (expirationDate?.getTime() ?? null);

    const [licenseHistory, setData] = useState<FetchLicenseHistory[]>([]);

    const {
        data: licenceHistory,
        isLoading: isLoadingLicenceHistory,
        isSuccess: isSuccessLicenceHistory,
        isError: isErrorLicenceHistory,
    } = useGetHistoryLicenseQuery({ licenseKey }, { skip: !licenseKey });

    useLayoutEffect(() => {
        dispatch(
            uiSlicedActions.setLoading(
                isLoadingLicenceHistory ||
                    isDisabling ||
                    isUpdatingExpiration
                    ? "pending"
                    : "hidden",
            ),
        );
    }, [
        isLoadingLicenceHistory,
        isDisabling,
        isUpdatingExpiration,
        dispatch,
    ]);

    useLayoutEffect(() => {
        if (isSuccessLicenceHistory && licenceHistory) {
            setData(licenceHistory);
        }
        if (isErrorLicenceHistory) {
            toast.error("Error fetching license history");
        }
    }, [licenceHistory, isSuccessLicenceHistory, isErrorLicenceHistory]);

    const disableHandler = () =>
        ConfirmationMessageBox({
            textContent: {
                title: "Are you sure you wish to disable this license?",
            },
        }).then(async (value) => {
            if (value !== "yes") return;
            if (!licenseKey) {
                toast.error("Missing license key");
                return;
            }
            try {
                const result = await disableIoloLicense(licenseKey).unwrap();
                toast.success(result.message ?? "License disabled");
            } catch (err) {
                const message =
                    (err as { data?: { message?: string } })?.data?.message ??
                    "Failed to disable license";
                toast.error(message);
            }
        });

    const handleSaveExpiration = async () => {
        if (!licenseKey || !expirationDate) return;

        const confirmation = await ConfirmationMessageBox({
            textContent: {
                title: `Update expiration to ${moment(expirationDate).format(
                    "LLL",
                )}?`,
            },
        });
        if (confirmation !== "yes") return;

        const payload = {
            licenseKey,
            expirationDate: expirationDate.toISOString(),
        };
        try {
            const result = await updateIoloExpiration(payload).unwrap();
            toast.success(result.message ?? "Expiration updated");
        } catch (err) {
            console.error(
                "[iolo-update] payload:",
                payload,
                "response:",
                err,
            );
            const message =
                (err as { data?: { message?: string } })?.data?.message ??
                "Failed to update expiration";
            toast.error(message);
        }
    };

    const handleRevertExpiration = () => {
        setExpirationDate(initialExpiration);
    };

    const noLicenseKey = !licenseKey;
    const alreadyDisabled = ioloInfo?.disabled === true;

    const actionButtons: IActionButton[] = [
        {
            styling: { type: "advanced", color: "red" },
            type: "button",
            name: "Disable",
            role: "secondary",
            disabled: noLicenseKey || alreadyDisabled || isDisabling,
            onClick: () => disableHandler(),
        },
    ];

    return (
        <div className="key_checker">
            <div className="key_checker__container">
                <div className="key_checker__col">
                    <div className="row">
                        <IoloLicenseKeysInformPanel info={ioloInfo} />
                    </div>

                    <div className="row">
                        <div
                            className={`iolo_expiration_editor ${
                                alreadyDisabled
                                    ? "iolo_expiration_editor--disabled"
                                    : ""
                            }`}
                        >
                            <div className="iolo_expiration_editor__title">
                                <div className="iolo_expiration_editor__title_text">
                                    Expiration date:
                                </div>
                                <div className="iolo_expiration_editor__title_value">
                                    {initialExpiration
                                        ? moment(initialExpiration).format(
                                              "LLL",
                                          )
                                        : "—"}
                                </div>
                            </div>

                            <div className="iolo_expiration_editor__form">
                                <div className="data_piker">
                                    <div className="form_control">
                                        <DatePicker
                                            className="form_control__input"
                                            selected={expirationDate}
                                            onChange={(date) =>
                                                setExpirationDate(
                                                    date as Date | null,
                                                )
                                            }
                                            showTimeSelect
                                            timeInputLabel="Time:"
                                            dateFormat="MM/dd/yyyy hh:mm aa"
                                            popperClassName="popup_data_piker"
                                            popperProps={{ strategy: "fixed" }}
                                            popperPlacement="bottom-start"
                                            placeholderText="Pick expiration"
                                            clearButtonClassName="popup_data_piker__action_button"
                                            disabled={
                                                noLicenseKey || alreadyDisabled
                                            }
                                            isClearable
                                        />
                                        <span className="animation-bg"></span>
                                    </div>
                                    <div className="data_piker__icon">
                                        <ImageSprite
                                            iconSprite={`${CalendarImg}#calendar`}
                                        />
                                    </div>
                                </div>

                                <ActivationLimitEditorActionButtons
                                    onSave={handleSaveExpiration}
                                    onReset={handleRevertExpiration}
                                    disabled={
                                        noLicenseKey ||
                                        alreadyDisabled ||
                                        !isDirty ||
                                        !expirationDate ||
                                        isUpdatingExpiration
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="key_checker__footer">
                <ActionButtons
                    className="form__button_submit"
                    actionButtons={actionButtons}
                />
            </div>

            <IoloLicenseKeyHistory licenseHistory={licenseHistory} />
        </div>
    );
};

export default IoloLicenseKeysSubRow;
